import type { Item } from '@/models';
import type { OU } from '@/models/Ou';
import type { SiteSettings, Source } from '@/models/site';
import { siteUtils } from '@/utils/siteUtils';

import { FuelEntityType, FuelPortalService } from '.';

const adPlatformFields: Source = {
  enabled: false,
  name: '',
  id: '',
  vmap: false,
  size: '',
  aps: {
    enabled: false,
    id: '',
    slotId: '',
    prerollId: '',
    prerollSlotId: '',
  },
  extras: '',
};

export function getDefaultSite(ouData: OU) {
  const defaultSite: SiteSettings = {
    entityType: 'Site',
    ouId: ouData.id,
    name: 'ViewNexa Test Site',
    description: 'A ViewNexa site for delivering a sublime viewing experience.',
    ownerName: 'John Doe',
    ownerEmail: 'JohnDoe@helloworld.com',
    supportName: 'Support Team',
    supportEmail: 'support@powr.com',
    timeZone: 'UTC-08:00',
    title: 'The Best Site Ever',
    domains: [],
    powrSiteCode: siteUtils.getSiteCode(ouData.ouName),
    data: {
      siteLayouts: [],
      linkedAssets: [],
      siteChannels: [],
      siteClips: [],
      siteCheckoutContents: [],
    },
    app: {
      favicon: '',
      logo: '',
      privacyPolicy: '',
      privacyPolicyText: '',
      shareImgLandscape: '',
      shareImgPortrait: '',
      tos: '',
      tosText: '',
      theme: 'classic',
      themeColor: 'dark',
      accent: '#5237A2',
      accentFont: 'black',
      accentColorFont: '#ffffff',
      social: {
        facebook: '',
        twitter: '',
        youtube: '',
        instagram: '',
      },
      widgetOrder: [
        {
          id: 'info',
          enabled: true,
        },
        {
          id: 'playqueue',
          enabled: true,
        },
        {
          id: 'share',
          enabled: true,
        },
        {
          id: 'comments',
          enabled: false,
        },
        {
          id: 'chat',
          enabled: false,
        },
        {
          id: 'watchparty',
          enabled: false,
        },
      ],
    },
    config: {
      // funnels: true,
      // hybridAvodSvod: true,
      // bitcentralMonetization: true,
      // globalMonetization: true,
      ageGate: false,
      // kidsContent: true,
      viewCounter: false,
      altResumeStyle: false,
      exactPublishedDate: false,
      // ingestOnly: true,
      embedRequiresLogin: false,
      geoblock: false,
      manualOrderLiveChannels: false,
      // episodesUploadFirst: true,
      hideAuth: false,
      // seriesNetworkShare: true,
      swapLogin: false,
      activateToLogin: false,
      stretchToFill: true,
      // hideLeftModule: true,
      hideRightModule: false,
      solidOverlay: false,
      stillWatching: true,
      backgroundPlay: false,
      theta: false,
      videoApi: false,
      // singleTrial: false,
      selfDelete: false,
      requireBillingAddress: false,
      hideUserDOB: true,
      hideUserGender: false,
      rudimentaryGender: true,
      // gifts: true,
      productBundles: false,
      passcodeBundles: false,
      rentals: false,
      // passwordAttempts: 3,
      // allowPasscode: true,
      // disableContentSharing: true,
    },
    integrations: {
      // not yet implemented. To review
      adBanners: [],
      ads: {
        videosFreqCap: 5,
        overrides: {},
        frequency: [],
        offsets: [],
        hybridAskInterval: 15,
        enabled: false,
        status: 'disabled',
        detect: true,
        source: {
          android: { ...adPlatformFields },
          androidTv: { ...adPlatformFields },
          appleTv: { ...adPlatformFields },
          fireTv: { ...adPlatformFields },
          ios: { ...adPlatformFields },
          vizioSmartCast: { ...adPlatformFields },
          web: { ...adPlatformFields },
          mobileWeb: { ...adPlatformFields },
        },
        txt: '',
        appTxt: '',
        // gumgum: [
        //   {
        //     enabled: false,
        //     platform: 'Android',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'AndroidTV',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'AppleTV',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'EricssonTV',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'FireTV',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'iOS',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'LGTV',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'Roku',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'SamsungTV',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'Via+',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'Vizio',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'Web',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'Mobile Web',
        //     id: '',
        //     extra: '',
        //   },
        //   {
        //     enabled: false,
        //     platform: 'Xbox',
        //     id: '',
        //     extra: '',
        //   },
        // ],
        // kargo: [
        //   {
        //     platform: 'Platform1',
        //     pid: 'pid1',
        //     msid: 'msid1',
        //     ads: 'ads1',
        //   },
        // ],
      },
      appPlatforms: {
        android: {
          store: {
            enabled: false,
          },
          deepLink: {
            enabled: false,
          },
        },
        ios: {
          store: {
            enabled: false,
          },
          deepLink: {
            enabled: false,
          },
        },
        roku: {
          enabled: false,
        },
        vizio: {
          enabled: false,
        },
      },
      argoid: {
        enabled: false,
        orgId: '',
        user: '',
        password: '',
      },
      avalara: {
        enabled: false,
        accountId: '',
        licenseKey: '',
        companyCode: '',
      },
      // bundleId: '',
      chat: {
        enabled: false,
        onLiveEvents: false,
        onLiveGuide: false,
        slowMode: false,
        limit: 100,
        threshold: 50,
        chatroll: false,
      },
      // drm: { // badly implemented on Integrations form
      //   enabled: false,
      //   fairplay: {
      //     version: '',
      //     licenseUrl: '',
      //     certificateUrl: '',
      //   },
      //   playready: {
      //     licenseUrl: '',
      //   },
      //   widevine: {
      //     licenseUrl: '',
      //   },
      // },
      // feedTtl: 60,
      // feePercentage: 10,
      heartbeat: {
        enabled: true,
      },
      piano: {
        enabled: false,
        appId: '',
        apiToken: '',
        purchasesOnPiano: false,
        usePianoId: false,
      },
      privacy: {
        didomi: {
          enabled: false,
          id: '',
          notice: '',
          // appNotices: [],
          // exclusionList: [],
          fallbackNotice: '',
        },
        // ketch: {
        //   organization: 'placeholder-organization',
        //   property: 'placeholder-property',
        //   dataSubjectTypeCodes: ['code1', 'code2'],
        //   apps: ['placeholder-app1', 'placeholder-app2'],
        // },
      },
      // profanityFilter: 'placeholder-profanity-filter',
      pubnub: {
        enabled: false,
        publishKey: '',
        subscribeKey: '',
        secretKey: '',
      },
      redirects: {
        enabled: false,
        url: '',
      },

      // segment: {
      //   enabled: true,
      //   writeKey: 'placeholder-write-key',
      // },
      trackers: {
        appsflyerKey: '',
        appsflyerSmartBannersKey: '',
        facebookPixels: [],
        googleAnalyticsId: '',
        gleam: '',
        googleTagManagerId: '',
        hotjar: '',
        optimizely: '',
        npawAnalytics: '',
        sailthru: '',
        segment: '',
      },
      tvSettings: {
        enabled: false,
        epgOnly: false,
        profileEnabled: true,
        splash: '',
      },
      watchparty: {
        enabled: false,
        movies: false,
        series: false,
        liveEvents: false,
      },
      // wurl: {
      //   uid: 'placeholder-uid',
      //   secret: 'placeholder-secret',
      // },
      // branchio: 'placeholder-branchio',
      // localizejs: 'placeholder-localizejs',
      // wowyow: 'placeholder-wowyow',
    },
    activeLayoutVersion: 1,
    activeLayoutPublicUrls: {},
    createdDate: '2025-03-10T00:00:00Z',
    modifiedDate: '2025-03-10T00:00:00Z',
  };

  return defaultSite;
}

export const getAssetIds = (site: SiteSettings) => [
  site.app.logo,
  site.app.favicon,
  site.app.shareImgLandscape,
  site.app.shareImgPortrait,
  site.integrations.tvSettings.splash,
];

export const fetchSiteAssets = async (sdParam: SiteSettings) => {
  const siteData = sdParam;
  const imageIds = [
    siteData.app.logo,
    siteData.app.favicon,
    siteData.app.shareImgLandscape,
    siteData.app.shareImgPortrait,
    siteData.integrations.tvSettings.splash,
  ];

  siteData.tmp = {
    images: {},
  };

  const siteAssets = (await FuelPortalService.batchGet(FuelEntityType.Clip, imageIds)) as Item[];
  siteAssets?.forEach((asset: Item) => {
    siteData.tmp.images[asset.id] = asset;
  });
};

export const fetchSite: (siteId: string) => Promise<SiteSettings> = async (siteId) => {
  const siteData: SiteSettings = await FuelPortalService.getEntityById(FuelEntityType.Site, siteId);
  if (!siteData) {
    throw new Error('Site is not defined');
  }

  await fetchSiteAssets(siteData);

  return siteData;
};

export const saveSite: (siteData: SiteSettings) => Promise<SiteSettings> = async (siteData) => {
  const result: SiteSettings = await FuelPortalService.upsertEntity(FuelEntityType.Site, siteData);

  // console.log('result', { result });

  if (result == null) {
    return null;
  }

  // ? do we need to getch on set?
  await fetchSiteAssets(result);

  return result;
};

export const validateSiteCode = async (siteCode: string): Promise<SiteSettings> => {
  try {
    const response = await FuelPortalService.validateSiteCode(siteCode);
    return response.data; // site code exists
  } catch (error: any) {
    const status = error?.response?.status;

    if (status === 404) {
      return null; // site code not found
    }
    // For other errors (500, network error)
    console.error('Unexpected error validating site code:', error);
    return null;
  }
};
